/*
  Daryada — comportements communs à tout le site.
  - En-tête : état « défilé » et ton clair/sombre selon la section en dessous
  - Menu mobile plein écran
  - Révélation des images et des textes à l'entrée dans l'écran
  - Léger décalage (parallaxe) des grandes images pleine largeur
*/
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* En-tête ------------------------------------------------------------ */
  const header = document.querySelector('[data-header]');

  const updateHeader = () => {
    if (!header) return;
    header.classList.toggle('is-scrolled', window.scrollY > 8);

    // Ton de la section située juste sous l'en-tête : les sections sombres
    // portent data-header-tone="dark", le logo passe alors en crème.
    const probeY = header.offsetHeight / 2;
    const probeX = window.innerWidth / 2;
    const under = document
      .elementsFromPoint(probeX, probeY)
      .find((el) => !header.contains(el) && !el.closest('[data-header-ignore]'));
    const toned = under && under.closest('[data-header-tone]');
    header.dataset.tone = toned ? toned.dataset.headerTone : 'light';
  };

  let headerTicking = false;
  const requestHeaderUpdate = () => {
    if (headerTicking) return;
    headerTicking = true;
    window.requestAnimationFrame(() => {
      updateHeader();
      headerTicking = false;
    });
  };

  if (header) {
    updateHeader();
    window.addEventListener('scroll', requestHeaderUpdate, { passive: true });
    window.addEventListener('resize', requestHeaderUpdate);
  }

  /* Menu mobile -------------------------------------------------------- */
  const menuToggle = document.querySelector('[data-menu-toggle]');
  const menu = document.getElementById('menu-mobile');

  if (menuToggle && menu) {
    const focusables = () =>
      menu.querySelectorAll('a[href], button:not([disabled]), input, select, [tabindex]:not([tabindex="-1"])');

    const setMenu = (open) => {
      menuToggle.setAttribute('aria-expanded', String(open));
      menu.hidden = !open;
      document.body.classList.toggle('is-locked', open);
      header.classList.toggle('is-menu-open', open);
      if (open) {
        const first = focusables()[0];
        if (first) first.focus();
      } else {
        menuToggle.focus();
      }
    };

    menuToggle.addEventListener('click', () => {
      setMenu(menuToggle.getAttribute('aria-expanded') !== 'true');
    });

    document.addEventListener('keydown', (event) => {
      if (menu.hidden) return;
      if (event.key === 'Escape') {
        setMenu(false);
        return;
      }
      // Garde le focus clavier dans le menu ouvert (bouton de fermeture inclus)
      if (event.key === 'Tab') {
        const items = [menuToggle, ...focusables()];
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });

    // Referme le menu si l'écran repasse en largeur bureau (en-tête complet)
    window.matchMedia('(min-width: 1200px)').addEventListener('change', (event) => {
      if (event.matches && !menu.hidden) setMenu(false);
    });
  }

  /* Révélations -------------------------------------------------------- */
  const revealAll = (elements) => elements.forEach((el) => el.classList.add('is-revealed'));

  const initReveal = (root = document) => {
    const elements = [...root.querySelectorAll('[data-reveal]:not(.is-revealed)')];
    if (!elements.length) return;

    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      revealAll(elements);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target); // une seule fois par élément
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
    );

    elements.forEach((el) => observer.observe(el));
  };

  /* Parallaxe des images pleine largeur (8 % maximum) ------------------- */
  const initParallax = () => {
    const items = [...document.querySelectorAll('[data-parallax]')];
    if (!items.length || reducedMotion.matches) return;

    let ticking = false;
    const update = () => {
      const viewport = window.innerHeight;
      items.forEach((item) => {
        const rect = item.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > viewport) return;
        // -1 quand l'élément entre par le bas, 1 quand il sort par le haut
        const progress = (viewport / 2 - (rect.top + rect.height / 2)) / (viewport / 2 + rect.height / 2);
        const shift = Math.max(-1, Math.min(1, progress)) * rect.height * 0.08;
        item.style.setProperty('--parallax', `${shift.toFixed(1)}px`);
      });
      ticking = false;
    };

    const request = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request);
  };

  /* Onglet de contact flottant ------------------------------------------
     Apparaît après 600 px de défilement. Sur mobile, s'efface tant que le
     bouton d'ajout au panier est à l'écran, pour ne jamais le masquer.
     Les logos (WhatsApp, Instagram, Facebook) se relaient toutes les 1,2 s
     pendant deux tours, puis toutes les 2,1 s, au même rythme sur ordinateur
     et sur téléphone, à compter de l'apparition de la pastille. Pause sous
     la souris, au focus clavier, panneau ouvert ou onglet du navigateur en
     arrière-plan ; reprise ensuite. Avec les animations réduites, le relais
     reste un simple fondu (CSS). */
  const initContactFlottant = () => {
    const root = document.querySelector('[data-contact-flottant]');
    if (!root) return;
    const toggle = root.querySelector('[data-contact-ouvrir]');
    const panel = root.querySelector('[data-contact-panneau]');
    const icons = [...root.querySelectorAll('[data-contact-icone]')];

    /* Logos qui se relaient */
    const INTERVALLE = 1200;
    const INTERVALLE_LENT = 2100;
    const CHANGEMENTS_RAPIDES = icons.length * 2;
    let changements = 0;
    let timer = null;
    let survol = false;
    let focusClavier = false;
    const tourne = () =>
      icons.length > 1 &&
      root.classList.contains('is-visible') &&
      panel.hidden &&
      !survol &&
      !focusClavier &&
      !document.hidden;
    const syncRotation = () => {
      if (tourne()) {
        const delai = changements < CHANGEMENTS_RAPIDES ? INTERVALLE : INTERVALLE_LENT;
        if (!timer) timer = window.setTimeout(step, delai);
      } else if (timer) {
        window.clearTimeout(timer);
        timer = null;
      }
    };
    const step = () => {
      timer = null;
      const current = icons.findIndex((icon) => icon.classList.contains('is-current'));
      icons[current].classList.remove('is-current');
      icons[(current + 1) % icons.length].classList.add('is-current');
      changements += 1;
      syncRotation();
    };
    // Souris seulement : sur écran tactile, un toucher n'est pas un survol.
    toggle.addEventListener('pointerenter', (event) => {
      survol = event.pointerType === 'mouse';
      syncRotation();
    });
    toggle.addEventListener('pointerleave', () => {
      survol = false;
      syncRotation();
    });
    toggle.addEventListener('focus', () => {
      focusClavier = toggle.matches(':focus-visible');
      syncRotation();
    });
    toggle.addEventListener('blur', () => {
      focusClavier = false;
      syncRotation();
    });
    document.addEventListener('visibilitychange', syncRotation);

    /* Apparition */
    let addButtonVisible = false;
    const mobile = window.matchMedia('(max-width: 989px)');
    const update = () => {
      const scrolled = window.scrollY > 600;
      const hide = mobile.matches && addButtonVisible && panel.hidden;
      root.classList.toggle('is-visible', (scrolled || !panel.hidden) && !hide);
      syncRotation();
    };
    const addButton = document.querySelector('[data-add-button]');
    if (addButton && 'IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        addButtonVisible = entries[0].isIntersecting;
        update();
      }).observe(addButton.closest('form') || addButton);
    }
    window.addEventListener('scroll', update, { passive: true });
    update();

    /* Panneau */
    const setPanel = (open, restoreFocus = true) => {
      panel.hidden = !open;
      toggle.setAttribute('aria-expanded', String(open));
      if (open) {
        const first = panel.querySelector('a[href]');
        if (first) first.focus();
      } else if (restoreFocus) {
        toggle.focus();
      }
      update();
    };
    toggle.addEventListener('click', () => setPanel(panel.hidden));
    panel.querySelector('[data-contact-fermer]').addEventListener('click', () => setPanel(false));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !panel.hidden) setPanel(false);
    });
    document.addEventListener('click', (event) => {
      if (!panel.hidden && !root.contains(event.target)) setPanel(false, false);
    });
  };

  initReveal();
  initParallax();
  initContactFlottant();

  // Éditeur de thème Shopify : les sections ajoutées ou rechargées
  // doivent apparaître immédiatement.
  document.addEventListener('shopify:section:load', (event) => {
    revealAll([...event.target.querySelectorAll('[data-reveal]')]);
    requestHeaderUpdate();
  });
})();
